import type { MapInfo } from '../types/LiveMap.types';

export interface PixelPoint {
  xPx: number;
  yPx: number;
}

export interface WorldPoint {
  x: number;
  y: number;
}

// Converts a world-frame point (meters, same frame as MapInfo.origin) to image pixels. `origin`
// is the ROS map_server convention (world pose of the image's bottom-left pixel; row 0 is the top).
export const worldToPixel = (point: WorldPoint, mapInfo: MapInfo): PixelPoint => {
  const [originX, originY] = mapInfo.origin;
  const rawYPx = (point.y - originY) / mapInfo.resolution;

  return {
    xPx: (point.x - originX) / mapInfo.resolution,
    yPx: mapInfo.verticalFlip ? mapInfo.imageHeightPx - rawYPx : rawYPx,
  };
};

const MILLIMETRES_PER_METRE = 1000;

// CV50 reports positions in millimetres; MapInfo (and the B50 data) are in metres.
export const millimetresToMetres = (millimetres: number): number => millimetres / MILLIMETRES_PER_METRE;

export const worldToPercent = (point: WorldPoint, mapInfo: MapInfo): { xPercent: number; yPercent: number } => {
  const { xPx, yPx } = worldToPixel(point, mapInfo);

  return {
    xPercent: (xPx / mapInfo.imageWidthPx) * 100,
    yPercent: (yPx / mapInfo.imageHeightPx) * 100,
  };
};

// Converts a world heading (radians, CCW from +x) into a CSS rotation in degrees (clockwise).
export const worldHeadingToScreenDegrees = (heading: number, mapInfo: MapInfo): number => {
  const degrees = (heading * 180) / Math.PI;
  return mapInfo.verticalFlip ? -degrees : degrees;
};

export const worldPointsToSvgPolylinePoints = (points: WorldPoint[], mapInfo: MapInfo): string =>
  points
    .map(point => {
      const { xPercent, yPercent } = worldToPercent(point, mapInfo);
      return `${xPercent},${yPercent}`;
    })
    .join(' ');

// Bridges CV50's Map georeference (mm-per-pixel Resolution, no origin offset) into MapInfo, so the
// same worldToPixel/worldToPercent helpers work for CV50. Best-effort, not a confirmed contract.
export const mapGeoreferenceToMapInfo = (
  resolutionMm: number,
  imageWidthPx: number,
  imageHeightPx: number
): MapInfo => ({
  resolution: resolutionMm / 1000,
  origin: [0, 0, 0],
  imageWidthPx,
  imageHeightPx,
  verticalFlip: false,
});
